/**
 * Máscaras aplicadas enquanto a pessoa digita.
 *
 * Papel: aqui ficam as funções que reescrevem o conteúdo de um campo a cada
 * tecla — pondo parênteses no telefone, pontos no CPF, barras na data —, as que
 * calculam para onde o cursor deve ir depois disso, e `MASCARAS`, que diz ao
 * `CampoMascarado` como cada formato vai do valor do formulário ao texto do
 * campo e de volta.
 *
 * Como usar: uma máscara nova entra aqui, ganha uma entrada em `MASCARAS` e é
 * aplicada pelo `CampoMascarado`, nunca chamada solta dentro de uma página.
 * Toda máscara é progressiva: recebe o que já está escrito, por incompleto que
 * esteja, e devolve a melhor forma possível daquilo — quem digita dois números
 * vê `(11`, e não um erro. E toda máscara é idempotente, de modo que reaplicá-la
 * ao próprio resultado não acrescente pontuação a cada tecla.
 *
 * Mascarar não valida. O texto mascarado é entregue ao schema de
 * `@concierge/shared`, que é quem limpa e recusa. A exceção é o formato de data
 * e de competência: o formulário recebe `AAAA-MM-DD` e `AAAA-MM` assim que a
 * digitação se completa, porque o `dataSchema` e o `competenciaSchema` aceitam
 * apenas ISO e o formato brasileiro não pode trafegar. Enquanto incompleto, o
 * texto segue como está e é o schema que o recusa.
 *
 * As três máscaras das telas da origem — `mascararCentavos`,
 * `mascararDecimalLivre` e `lerValorDigitado` — são a exceção à regra do
 * `CampoMascarado`: reproduzem `maskBRL`, `maskDecimalLivre` e `parseBRL` do
 * miolo de Finanças, e o campo que as usa as chama no próprio `onChange`, com o
 * cursor indo para o fim a cada tecla, como lá. Passá-las pelo `MASCARAS`
 * mudaria o que a pessoa vê: o `mascararDinheiro` esvazia o campo no zero, e o
 * `maskBRL` escreve `0,00`.
 */
import { converterDinheiro } from '@concierge/shared';

/** Quantos dígitos cada máscara aceita antes de parar de escrever. */
const LIMITES = {
  telefone: 11,
  cpf: 11,
  cep: 8,
  data: 8,
  placa: 7,
  competencia: 6,
  dinheiro: 13,
} as const;

/**
 * O que conta como caractere digitado na hora de reposicionar o cursor.
 *
 * `digitos` serve às máscaras que só aceitam números e acrescentam pontuação;
 * `alfanumericos` serve à placa, que mistura letras e números. Contar só
 * dígitos numa placa faria o cursor pular as letras.
 */
export type AncoraDoCursor = 'digitos' | 'alfanumericos';

/** Expressões que reconhecem um caractere de cada âncora. */
const RECONHECE: Record<AncoraDoCursor, RegExp> = {
  digitos: /\d/,
  alfanumericos: /[A-Za-z0-9]/,
};

/**
 * Descarta tudo o que não for dígito e corta no limite da máscara.
 *
 * @param texto - Conteúdo atual do campo.
 * @param limite - Quantos dígitos a máscara comporta.
 * @returns Os dígitos que cabem, na ordem em que apareciam.
 */
function digitosAte(texto: string, limite: number): string {
  return texto.replace(/\D/g, '').slice(0, limite);
}

/**
 * Escreve um telefone conforme ele é digitado.
 *
 * A quebra muda de lugar no décimo primeiro dígito, quando o número deixa de
 * ser fixo e passa a ser celular. É por isso que o hífen anda uma casa quando
 * se digita o último dígito de um celular — comportamento correto, e o único
 * que produz `(11) 3456-7890` e `(11) 98765-4321` com a mesma função.
 *
 * O código de país é descartado antes do corte em onze dígitos, com a mesma
 * regra do `telefoneOpcionalSchema`: mais de onze dígitos começando por `55`.
 * Cortar antes de descartar faria `+55 (11) 98765-4321`, colado de uma conversa,
 * aparecer como `(55) 11987-6543` — um número que existe e é de outra pessoa.
 * Um celular do DDD 55 tem exatamente onze dígitos e por isso não é afetado.
 *
 * @param texto - Conteúdo atual do campo, completo ou não.
 * @returns O texto mascarado, vazio quando não há dígito algum.
 */
export function mascararTelefone(texto: string): string {
  let todos = texto.replace(/\D/g, '');
  if (todos.length > LIMITES.telefone && todos.startsWith('55')) todos = todos.slice(2);
  const d = todos.slice(0, LIMITES.telefone);

  if (d.length === 0) return '';
  if (d.length <= 2) return `(${d}`;
  if (d.length <= 6) return `(${d.slice(0, 2)}) ${d.slice(2)}`;
  if (d.length <= 10) return `(${d.slice(0, 2)}) ${d.slice(2, 6)}-${d.slice(6)}`;
  return `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7)}`;
}

/**
 * Escreve um CPF conforme ele é digitado.
 *
 * @param texto - Conteúdo atual do campo, completo ou não.
 * @returns O texto mascarado, vazio quando não há dígito algum.
 */
export function mascararCpf(texto: string): string {
  const d = digitosAte(texto, LIMITES.cpf);
  if (d.length === 0) return '';
  if (d.length <= 3) return d;
  if (d.length <= 6) return `${d.slice(0, 3)}.${d.slice(3)}`;
  if (d.length <= 9) return `${d.slice(0, 3)}.${d.slice(3, 6)}.${d.slice(6)}`;
  return `${d.slice(0, 3)}.${d.slice(3, 6)}.${d.slice(6, 9)}-${d.slice(9)}`;
}

/**
 * Escreve um CEP conforme ele é digitado.
 *
 * @param texto - Conteúdo atual do campo, completo ou não.
 * @returns O texto mascarado, vazio quando não há dígito algum.
 */
export function mascararCep(texto: string): string {
  const d = digitosAte(texto, LIMITES.cep);
  if (d.length <= 5) return d;
  return `${d.slice(0, 5)}-${d.slice(5)}`;
}

/**
 * Escreve uma data conforme ela é digitada, em ordem brasileira.
 *
 * Aceita digitação sem barras — `31082026` vira `31/08/2026` —, que é como
 * quem preenche muitos registros seguidos costuma trabalhar.
 *
 * @param texto - Conteúdo atual do campo, completo ou não.
 * @returns O texto mascarado, vazio quando não há dígito algum.
 */
export function mascararData(texto: string): string {
  const d = digitosAte(texto, LIMITES.data);
  if (d.length <= 2) return d;
  if (d.length <= 4) return `${d.slice(0, 2)}/${d.slice(2)}`;
  return `${d.slice(0, 2)}/${d.slice(2, 4)}/${d.slice(4)}`;
}

/**
 * Escreve uma competência mensal conforme ela é digitada.
 *
 * @param texto - Conteúdo atual do campo, completo ou não.
 * @returns O texto mascarado no formato `MM/AAAA`.
 */
export function mascararCompetencia(texto: string): string {
  const d = digitosAte(texto, LIMITES.competencia);
  if (d.length <= 2) return d;
  return `${d.slice(0, 2)}/${d.slice(2)}`;
}

/**
 * Escreve uma placa conforme ela é digitada.
 *
 * Não põe separador, porque a frota mistura os dois padrões e o hífen só
 * pertence ao antigo — decidir por ele no quarto caractere obrigaria a
 * removê-lo no quinto, quando aparecesse a letra do Mercosul.
 *
 * @param texto - Conteúdo atual do campo, completo ou não.
 * @returns Os caracteres alfanuméricos em maiúsculas, no máximo sete.
 */
export function mascararPlaca(texto: string): string {
  return texto
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, '')
    .slice(0, LIMITES.placa);
}

/**
 * Escreve um valor em reais conforme ele é digitado.
 *
 * Os dígitos entram pela direita, como numa calculadora: quem digita `1`, `2`,
 * `3` vê `0,01`, `0,12`, `1,23`. É o que dispensa a pessoa de acertar a vírgula
 * no meio do número.
 *
 * Valor zero devolve vazio, e não `0,00`. Apagando `0,01` com a tecla de
 * retrocesso o texto passa por `0,0`, cujos dígitos formam zero; se zero fosse
 * escrito como `0,00`, o campo nunca chegaria a esvaziar e ficaria impossível
 * deixar um valor opcional em branco.
 *
 * Um `-` em qualquer ponto do texto torna o valor negativo, e o sinal é escrito
 * à frente. Descartá-lo junto com a pontuação faria um estorno vindo da API
 * aparecer positivo no campo e ser gravado positivo na primeira edição, sem que
 * ninguém tivesse mexido no valor.
 *
 * @param texto - Conteúdo atual do campo, completo ou não.
 * @returns O valor com separador de milhar, duas casas e o sinal quando
 *   negativo; vazio quando não há dígito algum ou quando os dígitos somam zero.
 */
export function mascararDinheiro(texto: string): string {
  const d = digitosAte(texto, LIMITES.dinheiro);
  const centavos = Number(d);
  if (d.length === 0 || centavos === 0) return '';
  const escrito = (centavos / 100).toLocaleString('pt-BR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return texto.includes('-') ? `-${escrito}` : escrito;
}

/**
 * Escreve um valor em reais a partir dos dígitos, como o `maskBRL` da origem.
 *
 * Os dígitos entram pela direita — `1`, `2`, `3` viram `0,01`, `0,12`, `1,23` —,
 * e todo o resto, sinal de menos inclusive, é descartado. Diferente de
 * `mascararDinheiro`, o zero digitado continua escrito: `0` vira `0,00`, e só o
 * campo sem dígito nenhum fica vazio. É o que mantém visível o `0,00` que a
 * pessoa digitou num desconto.
 *
 * @param texto - Conteúdo atual do campo, como a pessoa o deixou.
 * @returns O valor com separador de milhar e duas casas; vazio sem dígitos.
 */
export function mascararCentavos(texto: string): string {
  const digitos = texto.replace(/\D/g, '');
  if (digitos === '') return '';
  const centavos = Number.parseInt(digitos, 10);
  const reais = Math.floor(centavos / 100);
  return `${reais.toLocaleString('pt-BR')},${String(centavos % 100).padStart(2, '0')}`;
}

/**
 * Deixa passar um decimal com até quatro casas, como o `maskDecimalLivre` da
 * origem.
 *
 * Serve à diária, que sai de um salário dividido por dias — `83,3333` — e que,
 * presa a duas casas, faria a diária vezes os dias nunca fechar o total do mês.
 * Só filtra: tira o que não é dígito nem vírgula, junta as vírgulas depois da
 * primeira e corta a quinta casa. Não põe separador de milhar, e um ponto
 * digitado some.
 *
 * @param texto - Conteúdo atual do campo.
 * @returns O texto só com dígitos e no máximo uma vírgula seguida de até quatro
 *   dígitos.
 */
export function mascararDecimalLivre(texto: string): string {
  let valor = texto.replace(/[^\d,]/g, '');
  const partes = valor.split(',');
  if (partes.length > 2) valor = `${partes[0] ?? ''},${partes.slice(1).join('')}`;
  const virgula = valor.indexOf(',');
  if (virgula !== -1 && valor.length - virgula - 1 > 4) valor = valor.slice(0, virgula + 5);
  return valor;
}

/**
 * Lê o número de um campo escrito em formato brasileiro, como o `parseBRL` da
 * origem.
 *
 * Tira os pontos de milhar e troca a vírgula por ponto antes de ler; o que não
 * se lê como número vale zero. É por isso que o campo vazio e o `0,00` dão o
 * mesmo zero, e quem precisa distinguir os dois — a linha do perfil desmarcada
 * contra a marcada sem valor — olha a caixa de marcar, e não o número.
 *
 * @param texto - O conteúdo do campo, mascarado ou não.
 * @returns O número lido; zero quando não há número.
 */
export function lerValorDigitado(texto: string): number {
  const numero = Number.parseFloat(texto.replace(/\./g, '').replace(',', '.'));
  return Number.isNaN(numero) ? 0 : numero;
}

/**
 * Conta quantos caracteres digitados existem antes de uma posição do texto.
 *
 * @param texto - Conteúdo do campo antes da máscara ser reaplicada.
 * @param posicao - Índice do cursor dentro desse texto.
 * @param ancora - O que conta como caractere digitado; `digitos` quando omitido.
 * @returns Quantos caracteres da âncora ficam à esquerda do cursor.
 */
export function contarDigitosAte(
  texto: string,
  posicao: number,
  ancora: AncoraDoCursor = 'digitos',
): number {
  let contagem = 0;
  for (const caractere of texto.slice(0, posicao)) {
    if (RECONHECE[ancora].test(caractere)) contagem += 1;
  }
  return contagem;
}

/**
 * Descobre onde pôr o cursor depois de a máscara reescrever o campo.
 *
 * Existe porque devolver um texto novo ao `input` joga o cursor para o fim, e
 * quem tentar corrigir o terceiro dígito de um telefone acaba digitando no
 * décimo primeiro. Ancorar pela contagem de caracteres digitados, e não pelo
 * índice, mantém o cursor no lugar mesmo quando a máscara acrescenta ou remove
 * pontuação à esquerda dele.
 *
 * @param mascarado - Texto já reescrito pela máscara.
 * @param digitosAntes - Quantos caracteres da âncora devem ficar à esquerda do
 *   cursor, normalmente o resultado de `contarDigitosAte` sobre o texto anterior.
 * @param ancora - O que conta como caractere digitado; `digitos` quando omitido.
 * @returns Índice em que o cursor deve ser reposicionado.
 */
export function posicaoAposMascara(
  mascarado: string,
  digitosAntes: number,
  ancora: AncoraDoCursor = 'digitos',
): number {
  if (digitosAntes <= 0) return 0;

  let vistos = 0;
  for (let i = 0; i < mascarado.length; i += 1) {
    if (RECONHECE[ancora].test(mascarado[i] ?? '')) {
      vistos += 1;
      if (vistos === digitosAntes) return i + 1;
    }
  }

  return mascarado.length;
}

/**
 * Converte o texto digitado de uma data para ISO, quando está completo.
 *
 * @param texto - Texto do campo, mascarado como `DD/MM/AAAA` ou incompleto.
 * @returns `AAAA-MM-DD` com oito dígitos presentes; o próprio texto em qualquer
 *   outro caso, para o `dataSchema` recusá-lo com a mensagem dele.
 */
export function dataDigitadaParaIso(texto: string): string {
  const d = texto.replace(/\D/g, '');
  if (d.length !== LIMITES.data) return texto;
  return `${d.slice(4, 8)}-${d.slice(2, 4)}-${d.slice(0, 2)}`;
}

/**
 * Converte o texto digitado de uma competência para `AAAA-MM`, quando completo.
 *
 * @param texto - Texto do campo, mascarado como `MM/AAAA` ou incompleto.
 * @returns `AAAA-MM` com seis dígitos presentes; o próprio texto em qualquer
 *   outro caso, para o `competenciaSchema` recusá-lo.
 */
export function competenciaDigitadaParaIso(texto: string): string {
  const d = texto.replace(/\D/g, '');
  if (d.length !== LIMITES.competencia) return texto;
  return `${d.slice(2, 6)}-${d.slice(0, 2)}`;
}

/** Os formatos que o `CampoMascarado` sabe escrever. */
export type FormatoMascarado =
  'telefone' | 'cpf' | 'cep' | 'data' | 'competencia' | 'placa' | 'dinheiro';

/**
 * Como um formato vai do valor do formulário ao texto do campo e de volta.
 *
 * `exibir` e `extrair` precisam ser inversas sobre o texto já mascarado:
 * `exibir(extrair(aplicar(t)))` deverá devolver `aplicar(t)`. Quando não
 * devolve, o React encontra no `input` um texto diferente do que o campo acabou
 * de escrever, sobrescreve, e o cursor salta para o fim a cada tecla.
 */
export interface Mascara {
  /** Reescreve o texto digitado. */
  aplicar: (texto: string) => string;
  /** Escreve no campo o valor que o formulário guarda, vindo da API ou não. */
  exibir: (valor: string | number) => string;
  /** Transforma o texto mascarado no valor que o formulário passa a guardar. */
  extrair: (texto: string) => string;
  /** Onde o cursor se ancora; `fim` para o dinheiro, que cresce pela direita. */
  ancora: AncoraDoCursor | 'fim';
  /** Teclado virtual oferecido no celular. */
  modoTeclado: 'tel' | 'numeric' | 'decimal' | 'text';
}

/**
 * A configuração de cada formato mascarado.
 *
 * A data e a competência são as únicas cujo `exibir` reconhece ISO: é assim que
 * uma data vinda da API, `2026-08-31`, aparece no campo como `31/08/2026` em vez
 * de ter os dígitos remascarados como `20/26/0831`. O dinheiro passa pelo
 * `converterDinheiro` pelo mesmo motivo — `1234.5`, lido da coluna `numeric`,
 * teria seus dígitos lidos como centavos e viraria `123,45`.
 */
export const MASCARAS: Record<FormatoMascarado, Mascara> = {
  telefone: {
    aplicar: mascararTelefone,
    exibir: (valor) => mascararTelefone(String(valor)),
    extrair: (texto) => texto,
    ancora: 'digitos',
    modoTeclado: 'tel',
  },
  cpf: {
    aplicar: mascararCpf,
    exibir: (valor) => mascararCpf(String(valor)),
    extrair: (texto) => texto,
    ancora: 'digitos',
    modoTeclado: 'numeric',
  },
  cep: {
    aplicar: mascararCep,
    exibir: (valor) => mascararCep(String(valor)),
    extrair: (texto) => texto,
    ancora: 'digitos',
    modoTeclado: 'numeric',
  },
  data: {
    aplicar: mascararData,
    exibir: (valor) => {
      const texto = String(valor);
      const iso = /^(\d{4})-(\d{2})-(\d{2})$/.exec(texto);
      return iso === null ? mascararData(texto) : `${iso[3]}/${iso[2]}/${iso[1]}`;
    },
    extrair: dataDigitadaParaIso,
    ancora: 'digitos',
    modoTeclado: 'numeric',
  },
  competencia: {
    aplicar: mascararCompetencia,
    exibir: (valor) => {
      const texto = String(valor);
      const iso = /^(\d{4})-(\d{2})$/.exec(texto);
      return iso === null ? mascararCompetencia(texto) : `${iso[2]}/${iso[1]}`;
    },
    extrair: competenciaDigitadaParaIso,
    ancora: 'digitos',
    modoTeclado: 'numeric',
  },
  placa: {
    aplicar: mascararPlaca,
    exibir: (valor) => mascararPlaca(String(valor)),
    extrair: (texto) => texto,
    ancora: 'alfanumericos',
    modoTeclado: 'text',
  },
  dinheiro: {
    aplicar: mascararDinheiro,
    exibir: (valor) => {
      const numero = typeof valor === 'number' ? valor : converterDinheiro(valor);
      if (numero === null || !Number.isFinite(numero)) return '';
      const sinal = numero < 0 ? '-' : '';
      return mascararDinheiro(`${sinal}${Math.round(Math.abs(numero) * 100)}`);
    },
    extrair: (texto) => texto,
    ancora: 'fim',
    modoTeclado: 'decimal',
  },
};
